import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiMail, FiPhone, FiCalendar } from 'react-icons/fi';

const ContactSection = () => {
  const contactMethods = [
    {
      icon: FiMail,
      title: "Email Us",
      value: "hello@wethebillions.com",
      link: "mailto:hello@wethebillions.com",
      description: "Get a response within 24 hours"
    },
    {
      icon: FiPhone,
      title: "Call or Text",
      value: "(202) 656-0054",
      link: "tel:+12026560054",
      description: "Direct line to our team"
    },
    {
      icon: FiCalendar,
      title: "Book Online",
      value: "wethebillions.com",
      link: "https://wethebillions.neetocal.com/consultation",
      description: "Schedule your consultation"
    }
  ];

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-playfair font-bold text-gray-900 mb-6">
            Ready to Build Your Child's Learning Brain?
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Take the first step towards transforming your child's academic future. Schedule your consultation today.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-6">
                Get In Touch
              </h3>
              <div className="space-y-6">
                {contactMethods.map((method, index) => (
                  <motion.a
                    key={index}
                    href={method.link}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors duration-200 group"
                  >
                    <div className="w-12 h-12 bg-accent-magenta/10 rounded-full flex items-center justify-center group-hover:bg-accent-magenta/20 transition-colors duration-200">
                      <SafeIcon icon={method.icon} className="h-6 w-6 text-accent-magenta" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{method.title}</div>
                      <div className="text-accent-magenta font-medium">{method.value}</div>
                      <div className="text-sm text-gray-600">{method.description}</div>
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Benefits of Consultation */}
            <div className="bg-accent-light/30 p-6 rounded-xl">
              <h4 className="font-bold text-gray-900 mb-4">What to Expect in Your Consultation:</h4>
              <ul className="space-y-2 text-gray-700">
                <li>• Comprehensive discussion of your child's challenges</li>
                <li>• Initial assessment of executive function needs</li>
                <li>• Personalized recommendations and next steps</li>
                <li>• Clear explanation of our approach and timeline</li>
                <li>• Answers to all your questions</li>
              </ul>
            </div>
          </motion.div>

          {/* Booking Widget */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <div>
              <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-4">
                Schedule Your Consultation
              </h3>
              <p className="text-gray-600 mb-6">
                Book your free 30-minute consultation to discuss your child's learning needs.
              </p>
            </div>

            {/* Embedded Booking */}
            <div className="bg-gray-50 rounded-xl p-6">
              <iframe
                allowFullScreen
                height="600"
                width="100%"
                frameBorder="0"
                title="Book Your Consultation - We The Billions"
                src="https://wethebillions.neetocal.com/embed/5f6e78fe-d1ce-43f6-b1ce-797e0cec501a"
                className="rounded-lg"
              />
            </div>

            {/* Backup Link */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">
                Having trouble with the booking widget?
              </p>
              <a
                href="https://wethebillions.neetocal.com/consultation"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                Book Directly Here
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;